import {Component, ChangeDetectionStrategy} from "@angular/core";
import {NzMessageService} from "ng-zorro-antd/message";
import {NzTableQueryParams, NzTableSortOrder} from "ng-zorro-antd/table";
import {lastValueFrom} from "rxjs";
import {LogSearchResultDto, PagingSearchDto, PerFormaAPIService} from "src/app/services/pfapi.service";

export interface SortDto {
    key: string;
    value: NzTableSortOrder;
}

@Component({
    selector: "app-logs",
    templateUrl: "./logs.component.html",
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class LogsComponent {
    constructor(
        private readonly api: PerFormaAPIService,
        private message: NzMessageService
    ) {}

    logs: LogSearchResultDto = {
        Data: [],
        Total: 0
    } as LogSearchResultDto;

    loading = true;
    total = 0;
    pageSizeOptions = [10, 15, 20, 50];
    pageSize = 15;
    pageIndex = 1;

    private lastParams: NzTableQueryParams | null = null;

    // default to most recent logs first
    defaultSort: SortDto[] = [{key: "date", value: "descend"}];
    sort: SortDto[] = [...this.defaultSort];

    // tracks which rows are expanded to show the exception detail
    expandSet = new Set<number>();

    // free-text search — the backend filters on one of these columns (Contains)
    searchField = "message";
    searchValue = "";
    searchFields = [
        {label: "Message", value: "message"},
        {label: "Level", value: "level"},
        {label: "Logger", value: "logger"},
        {label: "Hostname", value: "hostname"}
    ];

    columns: {
        title: string;
        fieldName: string | null;
        width: string | null;
    }[] = [
        {title: "Date", fieldName: "date", width: "180px"},
        {title: "Level", fieldName: "level", width: "100px"},
        {title: "Logger", fieldName: "logger", width: "220px"},
        {title: "Message", fieldName: "message", width: null},
        {title: "Hostname", fieldName: "hostname", width: "160px"}
    ];

    GetData = async () => {
        this.loading = true;

        const params: PagingSearchDto = {
            PageIndex: this.pageIndex,
            PageSize: this.pageSize,
            SearchField: this.searchValue ? this.searchField : null,
            SearchValue: this.searchValue || null
        };

        const sort = this.sort.filter(s => s.value !== null);

        if (sort.length) {
            params.SortField = sort[0].key;
            params.SortOrder = sort[0].value ?? undefined;
        } else {
            params.SortField = this.defaultSort[0].key;
            params.SortOrder = this.defaultSort[0].value ?? undefined;
        }

        try {
            this.logs = await lastValueFrom(this.api.postApiConfigureLogsSearch(params));
            this.total = this.logs.Total || 0;
            this.expandSet.clear();
        } catch (error) {
            this.message.error("Failed to fetch logs");
            console.log(error);
        } finally {
            this.loading = false;
        }
    };

    async onQueryParamsChange(params: NzTableQueryParams): Promise<void> {
        if (this.isParamsSame(this.lastParams, params)) {
            return;
        }

        this.lastParams = {...params};

        this.pageIndex = params.pageIndex;
        this.pageSize = params.pageSize;

        // reset the page index if the sort changes
        if (this.sortChanged(params.sort)) {
            this.pageIndex = 1;
        }

        this.sort = [...params.sort];
        await this.GetData();
    }

    // nz-table likes to fire extraneous onQueryParamsChange events
    // so we need to filter out the ones that don't actually change anything
    private isParamsSame(a: NzTableQueryParams | null, b: NzTableQueryParams): boolean {
        if (!a) return false;
        const activeSortA = a.sort.filter(s => s.value !== null);
        const activeSortB = b.sort.filter(s => s.value !== null);
        return (
            a.pageIndex === b.pageIndex &&
            a.pageSize === b.pageSize &&
            activeSortA.length === activeSortB.length &&
            activeSortA.every((s, i) => s.key === activeSortB[i].key && s.value === activeSortB[i].value)
        );
    }

    private sortChanged(sort: SortDto[]): boolean {
        const newSort = sort.filter(s => s.value !== null);
        const currentSort = this.sort
            ? this.sort.filter(s => s.value !== null)
            : [...this.defaultSort].filter(s => s.value !== null);

        if (newSort.length === 0 && currentSort.length === 0) {
            return false;
        }

        if (newSort.length !== currentSort.length) {
            return true;
        }

        return newSort[0].key !== currentSort[0].key || newSort[0].value !== currentSort[0].value;
    }

    getSortOrder(fieldName: string | null): NzTableSortOrder {
        if (!fieldName || !this.sort) {
            return null;
        }
        const sortItem = this.sort.find(s => s.key === fieldName);
        return sortItem ? sortItem.value : null;
    }

    async onSearch(): Promise<void> {
        this.pageIndex = 1;
        await this.GetData();
    }

    async clearSearch(): Promise<void> {
        this.searchField = "message";
        this.searchValue = "";
        this.pageIndex = 1;
        this.sort = [...this.defaultSort];
        await this.GetData();
    }

    onExpandChange(id: number, checked: boolean): void {
        if (checked) {
            this.expandSet.add(id);
        } else {
            this.expandSet.delete(id);
        }
    }
}
